// AICompletion.tsx
import { Button, Input, Modal, Spin } from "antd";
import { useIntl } from "react-intl";
import React, { FC, useCallback, useState, useRef, useEffect } from "react";
import { SendOutlined } from "@ant-design/icons";
import style from "./style.module.css";
import axiosInstance from "../../../../../config/axiosConfig";

interface AICompletionProps {
  isOpen: boolean;
  onCancel: () => void;
}

interface Message {
  content: string;
  isAssistant: boolean;
}

const AICompletion: FC<AICompletionProps> = ({ isOpen, onCancel }) => {
  const { formatMessage } = useIntl();
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState<string>("");
  const [threadId, setThreadId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showToolInput, setShowToolInput] = useState<boolean>(false);
  const [pendingToolCalls, setPendingToolCalls] = useState<ChatCompletionToolCall[]>([]);
  const [toolResults, setToolResults] = useState<ChatCompletionToolResult[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom whenever messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const prepareRequestData = (question: string, completions: ChatCompletionToolResult[] = []): ThreadClientRequestDto => {
    // Get the user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;


    return {
      question: question,
      timezone: timezone,
      completions: completions
    };
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  interface ChatCompletionToolResult {
    toolCallId: string;
    functionName: string;
    resultJson: string;
  }

  interface ThreadClientRequestDto {
    question: string;
    timezone: string;
    completions: ChatCompletionToolResult[];
  }

  interface ChatCompletionToolCall {
    id: string;
    type: string;
    name: string;
    arguments: string;
  }

  // Define interface for the response data
  interface AssistantResponse {
    threadId: string;
    answer: string;
    toolCalls: ChatCompletionToolCall[];
  }

  const sendMessage = useCallback(async () => {
    if (!currentMessage.trim()) return;

    // Add user message to chat
    setMessages(prev => [...prev, { content: currentMessage, isAssistant: false }]);
    setLoading(true);

    try {
      let response: { data: AssistantResponse };
      let requestData: ThreadClientRequestDto;

      if (pendingToolCalls.length > 0) {
        // Create tool results from the user's message
        const completions = pendingToolCalls.map(toolCall => ({
          toolCallId: toolCall.id,
          functionName: toolCall.name,
          resultJson: currentMessage
        }));

        requestData = prepareRequestData("", completions);
        setPendingToolCalls([]); // Clear pending tool calls
      } else {
        requestData = prepareRequestData(currentMessage);
      }

      if (threadId) {
        // Continue conversation
        response = await axiosInstance.post<AssistantResponse>(`/api/ai/completion/${threadId}`, requestData);
      } else {
        // Create new thread
        response = await axiosInstance.post<AssistantResponse>("/api/ai/completion", requestData);
        setThreadId(response.data.threadId);
      }

      // Add assistant response to chat
      setMessages(prev => [...prev, { content: response.data.answer, isAssistant: true, toolCalls: response.data.toolCalls  }]);

      if (response.data.toolCalls && response.data.toolCalls.length > 0) {
        setPendingToolCalls(response.data.toolCalls);
        setShowToolInput(true);
        setToolResults([]); // Clear previous tool results
      } else {
        setPendingToolCalls([]);
        setShowToolInput(false);
        setToolResults([]);
      }

    } catch (error: unknown) {
      console.error("Error sending message:", error);
      // Add error message to chat
      setMessages(prev => [...prev, {
        content: formatMessage({ id: 'AI_COMPLETION_ERROR' }, { defaultMessage: "Sorry, I couldn't process your request. Please try again." }),
        isAssistant: true
      }]);
    } finally {
      setLoading(false);
      setCurrentMessage("");
    }
  }, [currentMessage, threadId, formatMessage, toolResults]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  return (
    <Modal
      title={<h3>{formatMessage({ id: 'AI_COMPLETION_ASSISTANT' }, { defaultMessage: "AI Assistant" })}</h3>}
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <div className={style.chatContainer}>
        {messages.length === 0 ? (
          <div className={style.emptyChat}>
            {formatMessage({ id: 'AI_COMPLETION_WELCOME' }, { defaultMessage: "How can I help you today?" })}
          </div>
        ) : (
          <div className={style.messagesContainer}>
            {messages.map((message, index) => (
              <div
                key={index}
                className={`${style.message} ${message.isAssistant ? style.assistantMessage : style.userMessage}`}
              >
                {message.content}
              </div>
            ))}
            {loading && (
              <div className={style.loadingContainer}>
                <Spin size="small" />
              </div>
            )}

            {pendingToolCalls.length > 0 && (
              <div className={style.toolCallNotification}>
                {formatMessage(
                  { id: 'AI_ASSISTANT_TOOL_NOTIFICATION' },
                  { defaultMessage: "The assistant needs more information. Your next message will be used to answer all tool calls." }
                )}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
        <div className={style.inputContainer}>
          <Input
            placeholder={ pendingToolCalls.length > 0
              ? formatMessage({ id: 'AI_COMPLETION_TOOL_PLACEHOLDER' })
              : formatMessage({ id: 'AI_COMPLETION_PLACEHOLDER' })
              }
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            className={style.messageInput}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={sendMessage}
            disabled={!currentMessage.trim() || loading}
            className={style.sendButton}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AICompletion;
